import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router";
import PolicyReplacementPreview from "work/policy-replacements/policy-replacement-preview";
import { revisionPath, workAgreementPath } from "work/policy-replacements/policy-replacement-routes";

const Group = styled("section")(({ theme }) => ({
  marginTop: theme.spacing(2),
}));
const GroupExplanation = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

type PolicyReplacementPreviewGroupsProps = {
  preview: PolicyReplacementPreview;
  selection: ReadonlySet<string>;
  disabled?: boolean;
  onToggle: (rowKey: string) => void;
};

/**
 * The preview, one section per group the design defines. A row the replacement can reach carries a
 * checkbox and starts checked; every other row is listed under the reason it is left out, so the
 * client sees each agreement it can see and is never left wondering where one went.
 */
export default function PolicyReplacementPreviewGroups(props: Readonly<PolicyReplacementPreviewGroupsProps>) {
  const { preview, selection, disabled, onToggle } = props;
  const groups = preview.groups;

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No running agreement holds this policy. Add one below if it holds a copy under another document.
      </Typography>
    );
  }

  return (
    <>
      {groups.map((group) => {
        const headingId = `policy-replacement-group-${group.scope}`;
        return (
          <Group key={group.scope} aria-labelledby={headingId}>
            <Typography id={headingId} variant="subtitle1" component="h3">
              {group.title} ({group.rows.length})
            </Typography>
            <GroupExplanation variant="body2">{group.explanation}</GroupExplanation>
            <List dense disablePadding>
              {group.rows.map((row) => {
                const key = PolicyReplacementPreview.rowKey(row);
                const checkboxId = `policy-replacement-row-${key}`;
                return (
                  <ListItem key={key} disableGutters>
                    {group.selectable && (
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selection.has(key)}
                          disabled={disabled}
                          onChange={() => onToggle(key)}
                          slotProps={{ input: { id: checkboxId, "aria-label": `Include ${row.name}` } }}
                        />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={
                        <Link component={RouterLink} to={workAgreementPath(row.workAgreementId)}>
                          {row.name}
                        </Link>
                      }
                      secondary={
                        row.openRevisionProposalId ? (
                          <Link component={RouterLink} to={revisionPath(row.openRevisionProposalId)}>
                            The revision already open
                          </Link>
                        ) : undefined
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Group>
        );
      })}
    </>
  );
}
